import { AlertTriangle, ArrowUpRight, CheckCircle } from "@untitledui/icons";
import { useEffect } from "react";
import { draftAuthoriseUrl, useMerchantDraftShare } from "@/api/merchant-draft-share";
import {
  CONNECTION_ERRORS,
  DRAFT_CONNECTION_MESSAGE,
  TikTokShopAccessReasons,
} from "@/components/add-merchant/step-connect-tiktok";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Button } from "@/components/base/buttons/button";
import { ConnectAdsShell } from "@/pages/connect-ads/connect-ads-shell";

const resultFromUrl = () => new URLSearchParams(window.location.search).get("result");

const isPopup = () => !!window.opener && window.opener !== window;

const Message = ({ tone, title, children }: { tone: "success" | "warning"; title: string; children: string }) => (
  <div
    role={tone === "warning" ? "alert" : "status"}
    className={
      tone === "success"
        ? "flex items-start gap-3 rounded-xl bg-success-25 p-6"
        : "flex items-start gap-3 rounded-xl border border-warning-300 bg-warning-25 p-6"
    }
  >
    {tone === "success" ? (
      <CheckCircle className="size-6 shrink-0 text-success-600" />
    ) : (
      <AlertTriangle className="size-6 shrink-0 text-warning-600" />
    )}
    <div className="flex flex-col gap-1">
      <p className="text-md font-semibold text-primary">{title}</p>
      <p className="text-sm text-tertiary">{children}</p>
    </div>
  </div>
);

const Expired = () => (
  <ConnectAdsShell>
    <div className="mx-auto max-w-[560px] text-center">
      <h1 className="text-xl font-medium text-primary">Nothing left to approve</h1>
      <p className="mt-1 text-sm text-tertiary">
        This shop has already been added to Unbox, or its setup was discarded. If you still need to approve access, ask
        your colleague for a new link.
      </p>
    </div>
  </ConnectAdsShell>
);

export function ConnectMerchantPage({ shareToken }: { shareToken: string }) {
  const result = resultFromUrl();
  const popup = isPopup() && !!result;
  const { data, isLoading, isError } = useMerchantDraftShare(shareToken);

  useEffect(() => {
    if (!popup) return;
    window.opener.postMessage({ type: DRAFT_CONNECTION_MESSAGE, result }, window.location.origin);
    window.close();
  }, [popup, result]);

  if (popup) {
    return (
      <ConnectAdsShell>
        <p className="text-center text-sm text-tertiary">You can close this window and go back to Unbox.</p>
      </ConnectAdsShell>
    );
  }

  if (isLoading) {
    return (
      <ConnectAdsShell>
        <div className="flex justify-center">
          <LoadingIndicator type="line-simple" size="md" />
        </div>
      </ConnectAdsShell>
    );
  }

  if (isError || !data || result === "link_expired") return <Expired />;

  const shopName = data.merchantName ?? (data.handle ? `@${data.handle}` : "this shop");
  const error = data.connectionError ? CONNECTION_ERRORS[data.connectionError] : null;

  const body = () => {
    if (data.shopConnected) {
      return (
        <Message tone="success" title="TikTok Shop connected">
          You can close this window. Your colleague will see the shop connected in Unbox.
        </Message>
      );
    }
    if (data.shopChoicePending || result === "choose_shop") {
      return (
        <Message tone="success" title="Access approved">
          TikTok gave Unbox access to more than one shop. Your colleague will choose which one to add in Unbox, so you
          can close this window.
        </Message>
      );
    }
    if (!data.credentialGroup || result === "region_missing") {
      return (
        <Message tone="warning" title="This link isn't ready yet">
          Your colleague still needs to choose where the shop is registered. Ask them to finish that step and open this
          link again.
        </Message>
      );
    }
    return (
      <>
        {error && (
          <Message tone="warning" title={error.title}>
            {error.body}
          </Message>
        )}
        <Button size="lg" iconTrailing={ArrowUpRight} href={draftAuthoriseUrl(shareToken)} className="self-start">
          {error ? "Try again on TikTok Shop" : "Log in to TikTok Shop"}
        </Button>
      </>
    );
  };

  return (
    <ConnectAdsShell>
      <div className="mx-auto flex max-w-[640px] flex-col gap-6">
        <div className="flex flex-col gap-1.5">
          <h1 className="text-display-xs font-medium text-primary">Approve Unbox's access to {shopName}</h1>
          <p className="text-sm text-tertiary">
            Someone on your team is adding {shopName} to Unbox. Only the shop's Primary account in TikTok Shop can
            approve access, so they've shared this link with you.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          <p className="text-sm font-semibold text-primary">What Unbox will have access to</p>
          <TikTokShopAccessReasons />
        </div>
        {body()}
      </div>
    </ConnectAdsShell>
  );
}
